import { useCalculationBaseColumnLabels } from "hooks/useCalculationBaseColumnLabels";
import { useMemo } from "react";

import type { DataType } from "./SalesComparisonChart/utils";

type DataTypeOption = { value: DataType; label: string };

/**
 * Fall back to turnover only once labels are loaded — otherwise a stored custom column would be
 * wiped while the company labels request is still in flight.
 */
export const resolveAnalyticsDataType = (
    dataType: DataType | undefined,
    dataTypeOptions: DataTypeOption[],
    labelsReady: boolean,
): DataType => {
    if (!dataType) return "turnover";
    if (dataType === "turnover" || dataType === "quantities") return dataType;
    if (!labelsReady) return dataType;
    return dataTypeOptions.some((option) => option.value === dataType) ? dataType : "turnover";
};

/**
 * Dimension options for analytics charts / channel metrics.
 * Custom turnover columns appear only once the company has named them (same rule as sales).
 */
export const useAnalyticsDataTypeOptions = () => {
    const { namedCustomTurnoverColumns, labelsStatus, resolveLabel } = useCalculationBaseColumnLabels();
    const labelsReady = labelsStatus === "succeeded" || labelsStatus === "failed";
    const turnoverLabel = resolveLabel("turnover");
    const quantityLabel = resolveLabel("quantity");

    const dataTypeOptions = useMemo(
        () => [
            { value: "turnover" as const, label: turnoverLabel },
            { value: "quantities" as const, label: quantityLabel },
            ...namedCustomTurnoverColumns.map(({ column, label }) => ({
                value: column,
                label,
            })),
        ],
        [namedCustomTurnoverColumns, quantityLabel, turnoverLabel],
    );

    const subtitleByDataType = useMemo(() => {
        const customSubtitles = Object.fromEntries(
            namedCustomTurnoverColumns.map(({ column, label }) => [column, label]),
        ) as Partial<Record<DataType, string>>;

        return {
            quantities: quantityLabel,
            turnover: turnoverLabel,
            ...customSubtitles,
        } as Record<DataType, string>;
    }, [namedCustomTurnoverColumns, quantityLabel, turnoverLabel]);

    return { dataTypeOptions, subtitleByDataType, labelsReady };
};
